import React, { useCallback, useEffect, useState } from "react";
import { Warning, BellRinging, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { getPushEnabled, syncSavedLocations } from "../lib/pushClient";

export default function PushHealthCard() {
  const { locations } = useSavedLocations();
  const [health, setHealth] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(false);
  const [fixFailed, setFixFailed] = useState(false);

  const clientId = typeof window !== "undefined" ? localStorage.getItem("firewatch:client_id") : null;
  const enabled = getPushEnabled();

  const check = useCallback(() => {
    if (!enabled || !clientId) return;
    api.get(`/push/health/${clientId}`).then(({ data }) => setHealth(data)).catch(() => {});
  }, [enabled, clientId]);

  useEffect(() => { check(); }, [check]);

  if (!enabled || !clientId || !health) return null;

  const broken = !health.found || (health.saved_locations_count === 0 && !health.admin_mode);
  if (!broken) {
    return (
      <div
        data-testid="push-health-ok"
        className="border border-tac-safe/40 bg-tac-safe/5 rounded-sm px-4 py-2.5 flex items-center gap-2 text-[13px] text-tac-safe"
      >
        <Check size={14} weight="bold" />
        Fire alerts healthy — watching {health.admin_mode ? "statewide (admin)" : `${health.saved_locations_count} saved location${health.saved_locations_count !== 1 ? "s" : ""}`}
      </div>
    );
  }

  const fixNow = async () => {
    setFixing(true);
    setFixFailed(false);
    try {
      await syncSavedLocations(locations);
      const { data } = await api.get(`/push/health/${clientId}`);
      setHealth(data);
      const ok = data.found && (data.saved_locations_count > 0 || data.admin_mode);
      setFixed(ok);
      setFixFailed(!ok);
    } catch (err) {
      setFixFailed(true);
    } finally {
      setFixing(false);
    }
  };

  return (
    <div
      data-testid="push-health-warning"
      className="border border-tac-warning/60 bg-tac-warning/10 rounded-sm p-4"
    >
      <div className="flex items-start gap-2.5">
        <Warning size={18} weight="fill" className="text-tac-warning shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-tac-warning">
            Your fire alerts aren't set up correctly
          </div>
          <div className="text-[13px] text-tac-dim mt-1 leading-relaxed">
            Notifications are turned on, but your alert subscription has{" "}
            <strong className="text-tac-text">no saved locations</strong> — you won't
            receive warnings when fires come near.{" "}
            {locations.length === 0
              ? "Add a saved location (star icon in the top bar), then come back here."
              : "Tap Fix Now to re-link your saved locations."}
          </div>
          {locations.length > 0 && (
            <button
              data-testid="push-health-fix-btn"
              onClick={fixNow}
              disabled={fixing || fixed}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-tac-warning text-black rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              <BellRinging size={13} weight="fill" />
              {fixing ? "Fixing…" : fixed ? "✓ Fixed" : "Fix Now"}
            </button>
          )}
          {fixFailed && (
            <div data-testid="push-health-fix-failed" className="mt-2 text-[12px] text-tac-danger">
              Couldn't re-link automatically. Open Saved Locations (star icon in the top
              bar), turn notifications off, then on again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
